// Typed convenience wrappers over callConsole for every RPC the UI uses.
// Kept as thin pass-throughs (no business logic) so pages import a
// readable function name instead of juggling generated Request classes
// directly everywhere; the actual auth/metadata/401-handling lives in
// client.ts's callConsole, not duplicated here.
import { callConsole } from '@/lib/grpcweb/client'
import * as pb from '@/generated/console_pb'
import type { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'

// ---------------- Accounts ----------------

export function listAccounts(req: {
  partnerFilter?: string
  nameContains?: string
  limit?: number
  offset?: number
}): Promise<pb.ListAccountsResponse> {
  const r = new pb.ListAccountsRequest()
  if (req.partnerFilter) r.setPartnerFilter(req.partnerFilter)
  if (req.nameContains) r.setNameContains(req.nameContains)
  r.setLimit(req.limit ?? 50)
  r.setOffset(req.offset ?? 0)
  return callConsole('listAccounts', r)
}

export function getAccount(accountId: string): Promise<pb.GetAccountResponse> {
  const r = new pb.GetAccountRequest()
  r.setAccountId(accountId)
  return callConsole('getAccount', r)
}

export function createAccount(req: {
  name: string
  partnerId?: string
  dealRegistrationId?: string
  idempotencyKey: string
}): Promise<pb.CreateAccountResponse> {
  const r = new pb.CreateAccountRequest()
  r.setName(req.name)
  if (req.partnerId) r.setPartnerId(req.partnerId)
  if (req.dealRegistrationId) r.setDealRegistrationId(req.dealRegistrationId)
  r.setIdempotencyKey(req.idempotencyKey)
  return callConsole('createAccount', r)
}

// ---------------- Instances ----------------

export function listInstances(req: {
  accountId?: string
  statusFilter?: pb.InstanceEnrollmentStatus
  deploymentTypeFilter?: pb.DeploymentType
  limit?: number
  offset?: number
}): Promise<pb.ListInstancesResponse> {
  const r = new pb.ListInstancesRequest()
  if (req.accountId) r.setAccountId(req.accountId)
  if (req.statusFilter !== undefined) r.setStatusFilter(req.statusFilter)
  if (req.deploymentTypeFilter !== undefined)
    r.setDeploymentTypeFilter(req.deploymentTypeFilter)
  r.setLimit(req.limit ?? 100)
  r.setOffset(req.offset ?? 0)
  return callConsole('listInstances', r)
}

export function getInstance(instanceId: string): Promise<pb.GetInstanceResponse> {
  const r = new pb.GetInstanceRequest()
  r.setInstanceId(instanceId)
  return callConsole('getInstance', r)
}

// ---------------- Licenses ----------------

export function listLicenses(req: {
  accountId?: string
  instanceId?: string
  statusFilter?: pb.LicenseStatus
  tierFilter?: pb.LicenseTier
  billingStateFilter?: pb.BillingState
  limit?: number
  offset?: number
}): Promise<pb.ListLicensesResponse> {
  const r = new pb.ListLicensesRequest()
  if (req.accountId) r.setAccountId(req.accountId)
  if (req.instanceId) r.setInstanceId(req.instanceId)
  if (req.statusFilter !== undefined) r.setStatusFilter(req.statusFilter)
  if (req.tierFilter !== undefined) r.setTierFilter(req.tierFilter)
  if (req.billingStateFilter !== undefined)
    r.setBillingStateFilter(req.billingStateFilter)
  r.setLimit(req.limit ?? 100)
  r.setOffset(req.offset ?? 0)
  return callConsole('listLicenses', r)
}

export function issueLicense(req: {
  accountId: string
  instanceId?: string
  deploymentType: pb.DeploymentType
  seatLimit: number
  connectivityMode: pb.ConnectivityMode
  telemetryRequired: boolean
  billingModel: pb.BillingModel
  billingState: pb.BillingState
  tier: pb.LicenseTier
  featureFlags?: string[]
  idempotencyKey: string
  orgScope?: pb.OrgScope
}): Promise<pb.IssueLicenseResponse> {
  const r = new pb.IssueLicenseRequest()
  r.setAccountId(req.accountId)
  if (req.instanceId) r.setInstanceId(req.instanceId)
  r.setDeploymentType(req.deploymentType)
  r.setSeatLimit(req.seatLimit)
  r.setConnectivityMode(req.connectivityMode)
  r.setTelemetryRequired(req.telemetryRequired)
  r.setBillingModel(req.billingModel)
  r.setBillingState(req.billingState)
  r.setTier(req.tier)
  if (req.featureFlags) r.setFeatureFlagsList(req.featureFlags)
  r.setIdempotencyKey(req.idempotencyKey)
  if (req.orgScope !== undefined) r.setOrgScope(req.orgScope)
  return callConsole('issueLicense', r)
}

export function assignLicense(
  licenseId: string,
  instanceId: string,
): Promise<pb.AssignLicenseResponse> {
  const r = new pb.AssignLicenseRequest()
  r.setLicenseId(licenseId)
  r.setInstanceId(instanceId)
  return callConsole('assignLicense', r)
}

export function suspendLicense(
  licenseId: string,
  reason: string,
): Promise<pb.SuspendLicenseResponse> {
  const r = new pb.SuspendLicenseRequest()
  r.setLicenseId(licenseId)
  r.setReason(reason)
  return callConsole('suspendLicense', r)
}

export function resumeLicense(licenseId: string): Promise<pb.ResumeLicenseResponse> {
  const r = new pb.ResumeLicenseRequest()
  r.setLicenseId(licenseId)
  return callConsole('resumeLicense', r)
}

export function revokeLicense(
  licenseId: string,
  reason: string,
): Promise<pb.RevokeLicenseResponse> {
  const r = new pb.RevokeLicenseRequest()
  r.setLicenseId(licenseId)
  r.setReason(reason)
  return callConsole('revokeLicense', r)
}

// ---------------- Operators ----------------

export function listOperators(req: {
  limit?: number
  offset?: number
}): Promise<pb.ListOperatorsResponse> {
  const r = new pb.ListOperatorsRequest()
  r.setLimit(req.limit ?? 100)
  r.setOffset(req.offset ?? 0)
  return callConsole('listOperators', r)
}

export function createOperator(req: {
  name: string
  email: string
  role: pb.ConsoleOperatorRole
}): Promise<pb.CreateOperatorResponse> {
  const r = new pb.CreateOperatorRequest()
  r.setName(req.name)
  r.setEmail(req.email)
  r.setRole(req.role)
  return callConsole('createOperator', r)
}

export function createApiKey(req: {
  operatorId: string
  name: string
}): Promise<pb.CreateApiKeyResponse> {
  const r = new pb.CreateApiKeyRequest()
  r.setOperatorId(req.operatorId)
  r.setName(req.name)
  return callConsole('createApiKey', r)
}

export function revokeApiKey(keyId: string): Promise<pb.RevokeApiKeyResponse> {
  const r = new pb.RevokeApiKeyRequest()
  r.setKeyId(keyId)
  return callConsole('revokeApiKey', r)
}

export function disableOperator(
  operatorId: string,
): Promise<pb.DisableOperatorResponse> {
  const r = new pb.DisableOperatorRequest()
  r.setOperatorId(operatorId)
  return callConsole('disableOperator', r)
}

// ---------------- Usage ----------------
//
// ListUsageEvents (ADR-118 Phase 5D, #1258) is the read side of D8's
// telemetry pipeline. account_id is REQUIRED (Console's RLS context is
// scoped per-account per transaction -- see console.proto's RPC doc).
// page_token is an opaque server-issued cursor; never construct one
// client-side.
export function listUsageEvents(req: {
  accountId: string
  instanceId?: string
  orgId?: string
  eventType?: string
  occurredAfter?: Timestamp
  occurredBefore?: Timestamp
  pageSize?: number
  pageToken?: string
}): Promise<pb.ListUsageEventsResponse> {
  const r = new pb.ListUsageEventsRequest()
  r.setAccountId(req.accountId)
  if (req.instanceId) r.setInstanceId(req.instanceId)
  if (req.orgId) r.setOrgId(req.orgId)
  if (req.eventType) r.setEventType(req.eventType)
  if (req.occurredAfter) r.setOccurredAfter(req.occurredAfter)
  if (req.occurredBefore) r.setOccurredBefore(req.occurredBefore)
  r.setPageSize(req.pageSize ?? 50)
  if (req.pageToken) r.setPageToken(req.pageToken)
  return callConsole('listUsageEvents', r)
}

// ---------------- Principal introspection ----------------

// WhoAmI (ADR-118 Phase 5D, #1258) returns the caller's own authenticated
// principal -- replaces the retired ListOperators-probe heuristic (see
// auth-context.tsx). Exactly one of operator/instance is populated on the
// response; the Console UI only ever authenticates as an operator, so
// callers here should read .getOperator().
export function whoAmI(): Promise<pb.WhoAmIResponse> {
  const r = new pb.WhoAmIRequest()
  return callConsole('whoAmI', r)
}

// ---------------- API keys ----------------

// ListApiKeys (ADR-118 Phase 5D, #1258) is admin-only (see console.proto's
// RPC doc) and lists key METADATA only -- never key material. operatorId
// empty means "every operator's keys."
export function listApiKeys(req: {
  operatorId?: string
  limit?: number
  offset?: number
}): Promise<pb.ListApiKeysResponse> {
  const r = new pb.ListApiKeysRequest()
  if (req.operatorId) r.setOperatorId(req.operatorId)
  r.setLimit(req.limit ?? 100)
  r.setOffset(req.offset ?? 0)
  return callConsole('listApiKeys', r)
}

// ---------------- Back-office dashboard (ADR-181 P1) ----------------

// listLicenseHealthAlerts (D2) returns the three license-health buckets an
// operator's daily sweep cares about: expiring soon, suspended, and
// seat-over-limit. expiringWithinDays<=0/unset defaults to 14 server-side
// (see the store layer's doc) -- this wrapper does not itself default the
// value, matching listLicenses' pass-through style above.
// ADR-185 Phase B adds two more buckets to this SAME call
// (org_scope_alerts, stale_heartbeat_alerts) rather than new RPCs -- see
// the proto doc. staleHeartbeatThresholdHours<=0/unset defaults to 24
// server-side, mirroring expiringWithinDays' own default-if-zero
// convention.
export function listLicenseHealthAlerts(req: {
  expiringWithinDays?: number
  staleHeartbeatThresholdHours?: number
}): Promise<pb.ListLicenseHealthAlertsResponse> {
  const r = new pb.ListLicenseHealthAlertsRequest()
  if (req.expiringWithinDays !== undefined) r.setExpiringWithinDays(req.expiringWithinDays)
  if (req.staleHeartbeatThresholdHours !== undefined)
    r.setStaleHeartbeatThresholdHours(req.staleHeartbeatThresholdHours)
  return callConsole('listLicenseHealthAlerts', r)
}

// getOrgUsageRollup (ADR-185 Phase B D6) is operator-only and
// cross-account by construction (no account_id parameter exists on this
// RPC -- see proto doc). Not wired into any UI drill-down yet in this
// build (out of the required scope per the ADR-185 Phase B build brief);
// exposed here so a future drill-down view has a ready wrapper.
export function getOrgUsageRollup(req: {
  instanceId?: string
  orgId?: string
  since?: Timestamp
  until?: Timestamp
}): Promise<pb.GetOrgUsageRollupResponse> {
  const r = new pb.GetOrgUsageRollupRequest()
  if (req.instanceId) r.setInstanceId(req.instanceId)
  if (req.orgId) r.setOrgId(req.orgId)
  if (req.since) r.setSince(req.since)
  if (req.until) r.setUntil(req.until)
  return callConsole('getOrgUsageRollup', r)
}

// inviteFirstCustomer (ADR-185 Phase B) is InviteMember's operator-only
// twin: mints the FIRST invite for an account with zero users. Admin-role
// operators only (server-enforced -- see server/customer_membership.go).
export function inviteFirstCustomer(req: {
  accountId: string
  email: string
  role: pb.AccountRole
}): Promise<pb.InviteFirstCustomerResponse> {
  const r = new pb.InviteFirstCustomerRequest()
  r.setAccountId(req.accountId)
  r.setEmail(req.email)
  r.setRole(req.role)
  return callConsole('inviteFirstCustomer', r)
}

// getRevenueOverview (D1's non-IBM slice) returns account/license/seat
// totals plus a per-(tier, billing_state) breakdown. Deliberately has no
// ibm_vs_direct split yet -- that is gated on ADR-180 D0's billing_channel
// column landing (see proto doc); the request currently takes no
// parameters.
export function getRevenueOverview(): Promise<pb.GetRevenueOverviewResponse> {
  const r = new pb.GetRevenueOverviewRequest()
  return callConsole('getRevenueOverview', r)
}

// ---------------- Customer requests (ADR-179 P3) ----------------

// listCustomerRequests is the operator-only inbox for the notify-operator
// RPCs a customer's RequestSeatChange/RequestCancellation write to. Never
// reachable by a CustomerIdentity (server/customer_identity.go never adds
// it to customerAllowedMethods) -- cross-account by design, so this
// wrapper takes no account context of its own beyond the optional filter.
export function listCustomerRequests(req: {
  statusFilter?: string
  accountIdFilter?: string
  kindFilter?: string
  // sourceFilter (ADR-186 Phase 2): "" | "portal" | "app" -- filters on
  // operator_requests.source (migration 0018).
  sourceFilter?: string
  pageSize?: number
  pageToken?: string
}): Promise<pb.ListCustomerRequestsResponse> {
  const r = new pb.ListCustomerRequestsRequest()
  if (req.statusFilter) r.setStatusFilter(req.statusFilter)
  if (req.accountIdFilter) r.setAccountIdFilter(req.accountIdFilter)
  if (req.kindFilter) r.setKindFilter(req.kindFilter)
  if (req.sourceFilter) r.setSourceFilter(req.sourceFilter)
  r.setPageSize(req.pageSize ?? 50)
  if (req.pageToken) r.setPageToken(req.pageToken)
  return callConsole('listCustomerRequests', r)
}

// acknowledgeCustomerRequest / resolveCustomerRequest implement ADR-186's
// operator-only lifecycle RPCs. Both are admin-only at the interceptor
// (neither RPC name is Get*/List*-prefixed, so ADR-119 D2's default role
// gate already restricts them) -- no client-side role check needed beyond
// the console shell's existing isAdmin-gated button rendering.
export function acknowledgeCustomerRequest(requestId: string): Promise<pb.AcknowledgeCustomerRequestResponse> {
  const r = new pb.AcknowledgeCustomerRequestRequest()
  r.setRequestId(requestId)
  return callConsole('acknowledgeCustomerRequest', r)
}

export function resolveCustomerRequest(requestId: string, note?: string): Promise<pb.ResolveCustomerRequestResponse> {
  const r = new pb.ResolveCustomerRequestRequest()
  r.setRequestId(requestId)
  if (note) r.setNote(note)
  return callConsole('resolveCustomerRequest', r)
}
